import { ReactNode } from 'react'
import { Navigate, Outlet, useMatches } from 'react-router-dom'
import { Box, Container, Theme, useMediaQuery } from '@mui/material'
import { Loading } from '@/components/loading'
import { CompanyContextProvider } from '@/context/companies'
import { useProduct } from '@/context/product'
import { routes } from '@/router'
import { ViewAsBanner } from '@/components/view-as-banner/view-as-banner'
import { isViewAs } from '@/helpers'
import { DesktopNavigation } from './desktop-navigation'
import { HeaderBar, HeaderSlotProvider } from './header-slot'
import { MobileNavigation } from './mobile-navigation'
import { useAccountingNavigation } from './navigation/use-accounting-navigation'
import { useCreditNavigation } from './navigation/use-credit-navigation'
import { NavigationContext, useNavigation } from './navigation/use-navigation'

type AppLayoutProps = {
  vertical?: 'accounting' | 'credit'
}

export function AppLayout({ vertical }: AppLayoutProps) {
  const { product, hasAccounting, accountingAvailable } = useProduct()
  const resolved =
    vertical ??
    (product === 'credit' || (accountingAvailable && !hasAccounting)
      ? 'credit'
      : 'accounting')

  if (resolved === 'credit') return <CreditAppLayout />
  return <AccountingAppLayout />
}

function AccountingAppLayout() {
  const { hasAccounting, accountingAvailable, accountingLoading } = useProduct()

  if (accountingLoading) {
    return <Loading />
  }

  if (accountingAvailable && !hasAccounting) {
    return <Navigate to={routes.credit.index} replace />
  }

  return (
    <CompanyContextProvider>
      <AccountingShell />
    </CompanyContextProvider>
  )
}

function AccountingShell() {
  const navigation = useAccountingNavigation()

  return (
    <NavigationContext.Provider value={navigation}>
      <AppShell />
    </NavigationContext.Provider>
  )
}

function CreditAppLayout() {
  const navigation = useCreditNavigation()

  return (
    <NavigationContext.Provider value={navigation}>
      <AppShell />
    </NavigationContext.Provider>
  )
}

function AppShell() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const { items, mobileItems, sidebarCard } = useNavigation()
  const focus = useMatches().some(
    (match) => (match.handle as { focus?: boolean } | undefined)?.focus,
  )

  return (
    <AppFrame
      pb={
        focus && isMobile
          ? 0
          : isMobile
            ? 'calc(112px + env(safe-area-inset-bottom, 0px))'
            : 2
      }
      navigation={
        focus && isMobile ? null : isMobile ? (
          <MobileNavigation items={mobileItems} />
        ) : (
          <DesktopNavigation items={items} sidebarCard={sidebarCard} />
        )
      }
    >
      <Outlet />
    </AppFrame>
  )
}

function AppFrame({
  children,
  navigation,
  pb = 2,
}: {
  children: ReactNode
  navigation?: ReactNode
  pb?: number | string
}) {
  return (
    <HeaderSlotProvider>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        height="100%"
      >
        {isViewAs() && <ViewAsBanner />}
        <Box display="flex" flex={1} minHeight={0} overflow="hidden">
          {navigation}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <HeaderBar />
            <Container
              component="main"
              maxWidth="md"
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                position: 'relative',
                pt: { xs: 2, md: 3 },
                pb,
              }}
            >
              {children}
            </Container>
          </Box>
        </Box>
      </Box>
    </HeaderSlotProvider>
  )
}
